import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Video,
  Mic,
  Building2,
  TrendingUp,
  Shield,
  Globe,
  BarChart3,
  ArrowRight,
  Check,
  Star,
  Users,
  Award,
  ChevronRight,
  Sparkles,
  Zap,
  Target,
  BookOpen,
  MessageCircle,
  type LucideIcon,
  Play,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useHeyGenToken } from '@/hooks/useHeyGenToken';
import { getAvatarConfig, type AvatarCompanyConfig } from './avatar-configs';

// Icon mapping for config-driven features
const iconMap: Record<string, LucideIcon> = {
  TrendingUp,
  Shield,
  Globe,
  BarChart3,
  BookOpen,
  Target,
  MessageCircle,
  Zap,
  Award,
  Users,
  Check,
  Star,
  Sparkles,
};

// UOB Five-Bar Gate Pattern Component (inspired by UOB's heritage logo)
function UOBGeometricPattern({ color }: { color: string }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Dynamic geometric background */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, ${color} 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />

      {/* Five vertical bars - inspired by UOB's five-bar gate motif - more dynamic now */}
      <div className="absolute right-[-10%] top-[-20%] h-[140%] flex gap-8 opacity-[0.08] rotate-12 transform origin-top-right">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="w-16 h-full"
            style={{
              background: `linear-gradient(180deg, ${color} 0%, transparent 100%)`,
              transform: `translateY(${i * 40}px)`,
            }}
          />
        ))}
      </div>

      {/* Subtle geometric accent lines */}
      <div className="absolute left-0 bottom-0 w-full h-full opacity-[0.05] pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke={color} strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
    </div>
  );
}

// Default features for each company type
const getDefaultFeatures = (config: AvatarCompanyConfig) => {
  if (config.features) {
    return config.features.map(f => ({
      icon: iconMap[f.icon] ? <IconComponent name={f.icon} /> : <BookOpen className="w-6 h-6" />,
      title: f.title,
      description: f.description,
    }));
  }

  // Fallback defaults
  if (config.id === 'boi') {
    return [
      { icon: <TrendingUp className="w-6 h-6" />, title: 'Personalized Portfolio', description: 'Get a custom ETF portfolio tailored to your goals and risk tolerance' },
      { icon: <Shield className="w-6 h-6" />, title: 'Secure & Regulated', description: 'Bank of Ireland is fully regulated by the Central Bank of Ireland' },
      { icon: <Globe className="w-6 h-6" />, title: 'Global Diversification', description: 'Access to global markets through low-cost ETFs' },
      { icon: <BarChart3 className="w-6 h-6" />, title: 'Track Performance', description: 'Monitor your portfolio performance in real-time' },
    ];
  }

  // Default for Grab and others
  return [
    { icon: <BookOpen className="w-6 h-6" />, title: 'Product Knowledge', description: 'Master core products and services' },
    { icon: <Target className="w-6 h-6" />, title: 'Interactive Training', description: 'Learn through quizzes and real-world scenarios' },
    { icon: <MessageCircle className="w-6 h-6" />, title: 'Practice Responses', description: 'Handle common queries with confidence' },
    { icon: <Zap className="w-6 h-6" />, title: 'Instant Feedback', description: 'Get corrections and tips in real-time' },
  ];
};

// Icon component helper
function IconComponent({ name, className = "w-6 h-6" }: { name: string; className?: string }) {
  const Icon = iconMap[name];
  return Icon ? <Icon className={className} /> : null;
}

// Default testimonials for each company type
const getDefaultTestimonials = (config: AvatarCompanyConfig) => {
  if (config.testimonials) {
    return config.testimonials;
  }

  // Fallback defaults
  if (config.id === 'boi') {
    return [
      { name: "Sarah O'Brien", age: '28', role: 'Marketing Manager', comment: 'The AI coach made investing so simple. I finally understand where my money is going!', rating: 5 },
      { name: 'James Murphy', age: '32', role: 'Software Developer', comment: "Started with €5,000 and now I'm consistently investing monthly. Great experience!", rating: 5 },
      { name: 'Emma Kelly', age: '25', role: 'Teacher', comment: 'Perfect for beginners like me. The portfolio suggestions were spot on.', rating: 5 },
    ];
  }

  // Default for Grab and others
  return [
    { name: 'Michelle Tan', role: 'Customer Service Rep', comment: 'The AI trainer helped me understand all products in one session. So much better than reading manuals!', rating: 5 },
    { name: 'Ahmad Rizal', role: 'Operations Specialist', comment: 'The interactive quizzes really helped me remember important details.', rating: 5 },
    { name: 'Priya Kumar', role: 'Support Agent', comment: 'I feel confident handling queries now. The training was clear and practical.', rating: 5 },
  ];
};

export function AvatarLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('company') || 'boi';
  const engine = searchParams.get('engine');

  // Get company configuration
  let config: AvatarCompanyConfig;
  try {
    config = getAvatarConfig(companyCode);
  } catch (error) {
    // If invalid company, redirect to BOI
    window.location.href = '/avatar?company=boi';
    return null;
  }

  const { generateToken, token } = useHeyGenToken();
  const hasPrefetchedRef = useRef(false);

  // Pre-fetch HeyGen token when landing page loads for faster video startup
  useEffect(() => {
    if (!hasPrefetchedRef.current && !token) {
      hasPrefetchedRef.current = true;
      console.log('[AvatarLanding] Pre-fetching HeyGen token for video mode...');
      generateToken().then(() => {
        console.log('[AvatarLanding] HeyGen token pre-fetched successfully');
      }).catch((error) => {
        console.error('[AvatarLanding] Failed to pre-fetch HeyGen token:', error);
      });
    } else if (token) {
      console.log('[AvatarLanding] HeyGen token already available (cached)');
    }
  }, [generateToken, token]);

  // Use config-driven features and testimonials
  const features = getDefaultFeatures(config);
  const testimonials = getDefaultTestimonials(config);

  // Get landing content with defaults
  const landingContent = config.landingContent || {
    featuresTitle: config.id === 'boi' ? 'Why Choose Bank of Ireland?' : `Why Train with ${config.name} AI?`,
    featuresSubtitle: config.id === 'boi'
      ? "Ireland's most trusted bank brings you modern investment solutions designed for the next generation"
      : 'Interactive AI training that prepares you for real interactions',
    testimonialsTitle: config.id === 'boi' ? 'Trusted by Young Investors' : 'Loved by Our Team',
    testimonialsSubtitle: config.id === 'boi'
      ? 'Join thousands already building their financial future with confidence'
      : `Hear from professionals who trained with ${config.avatar.coachName}`,
    ctaTitle: config.id === 'boi' ? 'Ready to Build Your Financial Future?' : `Ready to Master ${config.name} Operations?`,
    ctaSubtitle: config.id === 'boi'
      ? 'Join the next generation of Irish investors. Start with as little as €500 and get expert guidance every step of the way.'
      : 'Start your interactive training session now and become a confident professional.',
    footerDisclaimer: config.id === 'boi'
      ? 'Investment values can go down as well as up. Past performance is not indicative of future results. Bank of Ireland is regulated by the Central Bank of Ireland.'
      : 'This training tool is for demonstration purposes and should be supplemented with official training materials.',
  };

  const isUOB = config.id === 'uob';

  return (
    <div className="min-h-screen font-sans" style={{ background: config.colors.light }}>
      {/* Red accent bar for UOB */}
      {config.brandStyle?.accentPosition === 'top' && (
        <div
          className="h-1.5 w-full shadow-sm relative z-50"
          style={{ background: config.colors.accent }}
        />
      )}

      {/* Header */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${isUOB ? 'bg-white/90 backdrop-blur-md border-b border-slate-200' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* UOB heritage seal mark */}
              {config.brandStyle?.showHeritage && config.brandStyle?.heritageText ? (
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded flex items-center justify-center font-bold text-white text-xs shadow-md"
                    style={{ background: config.colors.accent }}
                  >
                    {config.brandStyle.heritageText.slice(0, 2)}
                  </div>
                  <div>
                    <h1 className="text-xl font-bold tracking-tight" style={{ color: config.colors.primary }}>
                      {config.name}
                    </h1>
                    <p className="text-xs text-slate-500 font-medium tracking-wide uppercase">{config.tagline}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-2 rounded-lg bg-slate-50">
                    <Building2 className="w-6 h-6" style={{ color: config.colors.primary }} />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold" style={{ color: config.colors.dark }}>
                      {config.name}
                    </h1>
                    <p className="text-xs text-slate-600">{config.tagline}</p>
                  </div>
                </>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/app')}
              className="text-sm font-medium hover:bg-slate-100"
            >
              Back to App
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative overflow-hidden min-h-[600px] flex items-center"
        style={{
          background: config.brandStyle?.heroPattern === 'geometric'
            ? `linear-gradient(135deg, ${config.colors.primary} 0%, ${config.colors.secondary} 100%)`
            : `linear-gradient(135deg, ${config.colors.primary} 0%, ${config.colors.secondary} 100%)`
        }}
      >
        {/* Background pattern based on brand style */}
        {config.brandStyle?.heroPattern === 'geometric' ? (
          <UOBGeometricPattern color="white" />
        ) : (
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-left"
            >
              {/* Heritage text for UOB */}
              {config.brandStyle?.showHeritage && config.brandStyle?.heritageText && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.8 }}
                  transition={{ delay: 0.2 }}
                  className="text-lg mb-6 font-medium tracking-widest uppercase text-white/80"
                >
                  {config.brandStyle.heritageText}
                </motion.p>
              )}

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 mb-8 shadow-lg"
              >
                <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
                <span className="text-sm font-medium text-white">{config.demo.badge}</span>
              </motion.div>

              <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 text-white leading-[1.1] tracking-tight">
                {config.demo.title}
              </h2>

              <p className="text-xl md:text-2xl text-white/90 max-w-xl mb-10 leading-relaxed font-light">
                {config.demo.subtitle}. {config.demo.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate(
                    config.id === 'uob'
                      ? `/avatar/scripted?company=${config.id}`
                      : `/avatar/video?company=${config.id}${engine ? `&engine=${engine}` : ''}`
                  )}
                  className="gap-3 px-8 py-7 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1 group"
                  style={{
                    background: 'white',
                    color: config.colors.primary,
                  }}
                >
                  <div className="p-1 rounded-full bg-slate-100 group-hover:bg-slate-200 transition-colors">
                    <Play className="w-4 h-4 fill-current" />
                  </div>
                  {config.demo.videoButtonText}
                </Button>

                <Button
                  size="lg"
                  onClick={() => navigate(`/avatar/voice?company=${config.id}`)}
                  className="gap-3 px-8 py-7 text-lg font-semibold border border-white/30 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 hover:bg-white/10"
                  style={{
                    background: 'transparent',
                    color: 'white',
                  }}
                >
                  <Mic className="w-5 h-5" />
                  {config.demo.voiceButtonText}
                </Button>
              </div>

              {/* Stats - show if config has stats */}
              {landingContent.stats && landingContent.stats.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="grid grid-cols-3 gap-8 mt-16 border-t border-white/10 pt-8"
                >
                  {landingContent.stats.map((stat, idx) => (
                    <div key={idx}>
                      <p className="text-3xl font-bold mb-1 text-white">{stat.value}</p>
                      <p className="text-sm text-white/70 uppercase tracking-wider font-medium">{stat.label}</p>
                    </div>
                  ))}
                </motion.div>
              )}
            </motion.div>

            {/* Right side visual - Abstract representation of the AI Coach */}
            <motion.div
               initial={{ opacity: 0, scale: 0.95 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.8, delay: 0.2 }}
               className="hidden lg:block relative"
            >
              <div className="relative aspect-square max-w-lg mx-auto">
                <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent rounded-full blur-3xl animate-pulse" />
                <div className="relative z-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-500">
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full blur-xl opacity-60" />

                  <div className="space-y-6">
                    <div className="flex items-center gap-4 border-b border-white/10 pb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center shadow-inner">
                        <Users className="w-8 h-8 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">{config.avatar.coachName}</h3>
                        <p className="text-white/80">{config.id === 'boi2' ? 'Helping you to navigate the world of wealth management' : `AI ${config.id === 'boi' ? 'Financial Coach' : 'Training Facilitator'}`}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-400 mt-2" />
                          <p className="text-white/90 text-sm leading-relaxed">
                            {config.id === 'boi2'
                              ? '"Hello! I\'m here to help answer any questions you may have on our products and services. What would you like to focus on today?"'
                              : `"Hello! I'm here to help you master ${config.id === 'uob' ? 'FAA regulations' : 'your goals'}. What would you like to focus on today?"`
                            }
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2 justify-end">
                        <div className="w-2 h-2 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>

                    <div className="pt-4 flex gap-3">
                       {landingContent.badges?.slice(0, 2).map((badge, idx) => (
                         <div key={idx} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white font-medium flex items-center gap-2">
                           <Check className="w-3 h-3" /> {badge.text}
                         </div>
                       ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h3 className="text-4xl font-bold mb-6 tracking-tight" style={{ color: config.colors.dark }}>
              {landingContent.featuresTitle}
            </h3>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {landingContent.featuresSubtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-transparent relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-slate-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative z-10">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-md transform group-hover:scale-110 transition-transform duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${config.colors.primary}10 0%, ${config.colors.secondary}10 100%)`
                    }}
                  >
                    <div style={{ color: config.colors.primary }}>
                      {feature.icon}
                    </div>
                  </div>
                  <h4 className="text-xl font-bold mb-4" style={{ color: config.colors.dark }}>
                    {feature.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Bottom accent line */}
                <div
                  className="absolute bottom-0 left-0 w-0 h-1 transition-all duration-300 group-hover:w-full"
                  style={{ background: config.colors.accent }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 relative overflow-hidden" style={{ background: config.colors.light }}>
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h3 className="text-4xl font-bold mb-6 tracking-tight" style={{ color: config.colors.dark }}>
              {landingContent.testimonialsTitle}
            </h3>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {landingContent.testimonialsSubtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-10 shadow-xl border border-slate-100 relative"
              >
                <div className="absolute top-8 right-8 text-slate-200">
                  <MessageCircle className="w-12 h-12 opacity-20" />
                </div>

                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-current"
                      style={{ color: config.colors.accent }}
                    />
                  ))}
                </div>

                <p className="text-slate-700 mb-8 text-lg italic leading-relaxed">
                  "{testimonial.comment}"
                </p>

                <div className="flex items-center gap-4 border-t border-slate-100 pt-6">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md"
                    style={{ background: config.colors.primary }}
                  >
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">
                      {testimonial.name}{('age' in testimonial) ? `, ${testimonial.age}` : ''}
                    </p>
                    <p className="text-sm text-slate-500 font-medium">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${config.colors.primary} 0%, ${config.colors.secondary} 100%)`
        }}
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-8 tracking-tight">
              {landingContent.ctaTitle}
            </h3>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              {landingContent.ctaSubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <Button
                size="lg"
                onClick={() => navigate(
                  config.id === 'uob'
                    ? `/avatar/scripted?company=${config.id}`
                    : `/avatar/video?company=${config.id}${engine ? `&engine=${engine}` : ''}`
                )}
                className="gap-3 px-10 py-8 text-lg font-bold shadow-2xl hover:shadow-3xl transition-all transform hover:scale-105 rounded-xl"
                style={{
                  background: 'white',
                  color: config.colors.primary,
                }}
              >
                <Video className="w-6 h-6" />
                {config.demo.videoButtonText}
                <ArrowRight className="w-6 h-6" />
              </Button>

              <Button
                size="lg"
                onClick={() => navigate(`/avatar/voice?company=${config.id}`)}
                className="gap-3 px-10 py-8 text-lg font-bold border-2 shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 rounded-xl hover:bg-white/10"
                style={{
                  background: 'transparent',
                  borderColor: 'white',
                  color: 'white',
                }}
              >
                <Mic className="w-6 h-6" />
                {config.demo.voiceButtonText}
                <ChevronRight className="w-6 h-6" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 text-white/80 border-t border-white/10 pt-10">
              {landingContent.badges ? (
                landingContent.badges.map((badge, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <IconComponent name={badge.icon} className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">{badge.text}</span>
                  </div>
                ))
              ) : config.id === 'boi' ? (
                <>
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">Regulated by CBI</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">50K+ Customers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">Bank Guarantee</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">4 Products Covered</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">20-Min Training</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5" />
                    <span className="text-sm font-semibold tracking-wide uppercase">Interactive Quizzes</span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-16 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-slate-50 rounded-lg">
                <Building2 className="w-6 h-6" style={{ color: config.colors.primary }} />
              </div>
              <div>
                <span className="text-lg font-bold block" style={{ color: config.colors.primary }}>
                  {config.name}
                </span>
                <span className="text-xs text-slate-500">
                  © 2025 Hupo. Powered by AI.
                </span>
              </div>
            </div>
            <div className="flex gap-8">
              <a href="#" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
                Contact Us
              </a>
            </div>
          </div>
          <div className="mt-10 p-6 bg-slate-50 rounded-xl border border-slate-100">
            <p className="text-xs text-slate-500 text-center leading-relaxed">
              <strong className="text-slate-700">Demo Notice:</strong> This is a prototype demonstration for {config.name}.
              {' '}{landingContent.footerDisclaimer}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
